import { Card, Flex, Heading, Skeleton } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { BallPreview } from 'components/common/ball-preview';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonVideoPreview } from 'components/common/video/preview';
import { t } from 'i18next';
import { VideoChangedFn } from 'interfaces/function-types';
import { ITVDefinition } from 'interfaces/traj-visualizer/i-definition';
import { GROUP_TARGET } from 'interfaces/traj-visualizer/i-group-definition';
import { VideoHelper } from 'lib_ts/classes/video.helper';
import { DEFAULT_DELIVERY, DeliveryType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IPitchVideos } from 'lib_ts/interfaces/pitches';
import { useCallback, useMemo, useState } from 'react';
import { usePitchDesignStore } from 'stores/use-pitch-design-store';
import { useVideosStore } from 'stores/use-videos-store';

interface IProps {
  loading?: boolean;
}

export const PDSidebar = (props: IProps) => {
  const [delivery, setDelivery] = useState(DEFAULT_DELIVERY);

  const inputs = usePitchDesignStore((s) => s.inputs);
  const derivedBs = usePitchDesignStore((s) => s.derivedBs);
  const derivedTraj = usePitchDesignStore((s) => s.derivedTraj);

  /** provide undefined video_id to clear selection */
  const onVideoChange = useCallback<VideoChangedFn>(
    async (videoID) => {
      try {
        const payload: Partial<IPitchVideos> = {};

        switch (delivery) {
          case DeliveryType.Stretch: {
            payload.stretchVideoID = videoID;
            break;
          }

          case DeliveryType.Windup:
          default: {
            payload.windupVideoID = videoID;
            break;
          }
        }

        // value is null => unset the value(s)
        if (!videoID) {
          usePitchDesignStore
            .getState()
            .mergeInputs({ videoIDs: { ...inputs.videoIDs, ...payload } });
          return;
        }

        // validate the video
        const video = useVideosStore
          .getState()
          .videos.find((v) => v._id === videoID);

        if (!video) {
          NotifyHelper.error({
            message_md: t('videos.video-x-not-in-context', { x: videoID }),
          });
          return;
        }

        usePitchDesignStore
          .getState()
          .mergeInputs({ videoIDs: { ...inputs.videoIDs, ...payload } });
      } catch (e) {
        console.error(e);
      }
    },
    [delivery, inputs.videoIDs]
  );

  const videoID =
    VideoHelper.getPitchDeliveryVideoID(inputs.videoIDs, delivery) ?? null;

  const trajModels = useMemo(() => {
    const output: ITVDefinition = {
      guid: 'main',
      description: 'common.target-trajectory-of-the-pitch',
      button_group: GROUP_TARGET,
      color: GROUP_TARGET.color,
      traj: derivedTraj,
      bs: derivedBs,
    };
    return [output];
  }, [derivedBs, derivedTraj]);

  return (
    <ErrorBoundary identifier="PitchDesignSidebar">
      <Flex
        direction="column"
        gap={RADIX.FLEX.GAP.MD}
        height={{ initial: 'auto', lg: '100%' }}
      >
        <Skeleton loading={!!props.loading}>
          <Card size="2" style={{ flex: 1 }}>
            <Flex
              direction="column"
              gap={RADIX.FLEX.GAP.SM}
              className="rt-VertFlexColumn"
              height="100%"
            >
              <Heading size={RADIX.HEADING.SIZE.MD}>
                {t('pd.ball-preview')}
              </Heading>

              {trajModels && (
                <BallPreview
                  spin={{
                    wx: derivedBs.wx,
                    wy: derivedBs.wy,
                    wz: derivedBs.wz,
                  }}
                  orientation={inputs.seams}
                  trajModels={trajModels}
                />
              )}
            </Flex>
          </Card>
        </Skeleton>

        <Skeleton loading={!!props.loading}>
          <Card size="2">
            <CommonVideoPreview
              identifier="PitchDesignVideoPreview"
              previewPx={inputs.releasePos.px}
              previewPz={inputs.releasePos.pz}
              videoID={videoID}
              selectConfig={{
                releasePx: inputs.releasePos.px,
                onChange: onVideoChange,
              }}
              delivery={delivery}
              onChangeDelivery={setDelivery}
            />
          </Card>
        </Skeleton>
      </Flex>
    </ErrorBoundary>
  );
};
